import { BrokeragePricing } from '../slices/marketplace/pricing/models/brokerage-pricing.model';
import { MarketplacePricing } from '../slices/marketplace/pricing/models/marketplace-pricing.model';
import { TransactionFeeScheduleItem } from '../slices/marketplace/pricing/models/transaction-fee-schedule-item.model';
import { LevelOfService } from '../slices/marketplace/timeline/models/enums/level-of-service.enum';
import { EngagementDocumentType } from '../wallet/models/EngagementDocumentType';

export interface EngagementPaymentPricingDetails {
    pricePerFteDvm: number;
    fteDvmsCount: number;
    creditablePercent: number;
    creditableAmount: number;
    totalPrice: number;
    engagementId: number;
    eventId: string;
    documentType?: EngagementDocumentType;
    documentId?: string;
}

export interface ServicePriceDetails {
    pricePerItem: number;
    itemsCount: number;
    totalPrice: number;
    creditablePercent: number;
    creditableAmount: number;
    firstOccurrencePrice?: number;
    baseFee?: number;
    fteDvms?: number;
}

export class PricesCalculationHelper {
    public static getBrokeragePricingByLevelOfService(
        levelOfService: LevelOfService,
        pricingState: MarketplacePricing
    ): BrokeragePricing {
        switch (levelOfService) {
            case LevelOfService.DiscountBrokerage:
                return pricingState.discountBrokeragePricing;
            case LevelOfService.FullServiceBrokerage:
                return pricingState.fullServiceBrokeragePricing;
            case LevelOfService.StrategicFinancialAdvisoryService:
                return pricingState.sfasPricing;
            default:
                throw new Error(
                    `Unsupported level of service: ${levelOfService}`
                );
        }
    }

    public static calculateSessionPrice(
        selectedNow: number,
        alreadyPurchased: number,
        bundleSize: number,
        pricePerItem: number
    ): number {
        let sessionPrice = 0;

        if (alreadyPurchased > 0) {
            const remainingFreeItems = bundleSize - alreadyPurchased;
            const freeItemsThisSession = Math.max(
                0,
                Math.min(selectedNow, remainingFreeItems)
            );

            const paidItems = selectedNow - freeItemsThisSession;

            sessionPrice = paidItems * pricePerItem;

            return sessionPrice;
        }

        const remainingItems = selectedNow - 1;
        const remainingFreeItems = bundleSize - 1;
        const freeItemsThisSession = Math.max(
            0,
            Math.min(remainingItems, remainingFreeItems)
        );
        const paidItems = remainingItems - freeItemsThisSession;

        sessionPrice = paidItems * pricePerItem + pricePerItem;

        return sessionPrice;
    }

    public static getIdNDAExecutionFeeDetails(
        pricingState: MarketplacePricing,
        fteDvmsCount: number,
        levelOfService: LevelOfService
    ): ServicePriceDetails {
        const brokerage = this.getBrokeragePricingByLevelOfService(levelOfService, pricingState);
        const pricePerItem = brokerage.buyersFees.sellerIdFeePerDvm;
        const totalPrice = pricePerItem * fteDvmsCount;
        return { pricePerItem, itemsCount: fteDvmsCount, totalPrice, creditablePercent: 0, creditableAmount: 0 };
    }

    public static getDataNDAExecutionFeeDetails(
        pricingState: MarketplacePricing,
        fteDvmsCount: number,
        levelOfService: LevelOfService
    ): ServicePriceDetails {
        const brokerage = this.getBrokeragePricingByLevelOfService(levelOfService, pricingState);
        const pricePerItem = brokerage.buyersFees.pipAccessFeePerDvm;
        const totalPrice = pricePerItem * fteDvmsCount;
        return { pricePerItem, itemsCount: fteDvmsCount, totalPrice, creditablePercent: 0, creditableAmount: 0 };
    }

    public static getFinancialModelFeeDetails(
        pricingState: MarketplacePricing,
        fteDvmsCount: number,
        levelOfService: LevelOfService
    ): ServicePriceDetails {
        const brokerage = this.getBrokeragePricingByLevelOfService(levelOfService, pricingState);
        const pricePerItem = brokerage.buyersFees.dfmAccessFeePerDvm;
        const totalPrice = pricePerItem * fteDvmsCount;
        return { pricePerItem, itemsCount: fteDvmsCount, totalPrice, creditablePercent: 0, creditableAmount: 0 };
    }

    public static getScheduleRow(
        schedule: TransactionFeeScheduleItem[],
        revenue: number
    ): TransactionFeeScheduleItem | undefined {
        const sorted = [...schedule].sort((a, b) => a.revenueMax - b.revenueMax);
        return sorted.find(s => revenue < s.revenueMax) ?? sorted[sorted.length - 1];
    }

    public static getLateFeePrice(
        pricePerDay: number,
        extansionDays: number,
        firstOccurrencePrice: number,
        isFirstOccurrence: boolean
    ): ServicePriceDetails {
        let totalPrice = 0;
        let remainingExtensionDays = extansionDays;
        if (isFirstOccurrence) {
            totalPrice = firstOccurrencePrice;
            remainingExtensionDays--;
        }
        totalPrice += pricePerDay * remainingExtensionDays;
        return {
            pricePerItem: pricePerDay,
            itemsCount: extansionDays,
            totalPrice: totalPrice,
            creditablePercent: 0,
            creditableAmount: 0,
            firstOccurrencePrice: firstOccurrencePrice,
        };
    }

    public static getSuspensionFeePrice(
        pricing: BrokeragePricing,
        revenue: number,
        monthesCount: number
    ): ServicePriceDetails {
        const row = PricesCalculationHelper.getScheduleRow(pricing.transactionFeeSchedule, revenue);
        const total = row?.suspensionFee ?? 0;
        return {
            pricePerItem: total,
            itemsCount: monthesCount,
            totalPrice: total,
            creditablePercent: 0,
            creditableAmount: 0,
        };
    }

    public static getChengeTempoFeePrice(
        pricing: BrokeragePricing,
        revenue: number
    ): ServicePriceDetails {
        const row = PricesCalculationHelper.getScheduleRow(pricing.transactionFeeSchedule, revenue);
        const total = row?.tempoChangeFee ?? 0;
        return {
            pricePerItem: total,
            itemsCount: 1,
            totalPrice: total,
            creditablePercent: 0,
            creditableAmount: 0,
        };
    }
}
